import { cn } from 'src/utils/cn';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface BadgeProps {
  readonly className?: string;
  readonly variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger';
  readonly text: string;
  readonly onClick?: () => void;
  readonly onRemove?: () => void;
  readonly removeTitle?: string;
  readonly title?: string;
  readonly id?: string;
  readonly childElement?: React.ReactNode;
}

export const Badge = ({
  text,
  variant = 'default',
  className = '',
  title = '',
  id = 'btn-badge',
  childElement,
  onClick,
  onRemove,
  removeTitle,
  ...props
}: BadgeProps) => {
  const isInteractive = typeof onClick === 'function';
  const isRemovable = typeof onRemove === 'function';
  const commonProps = {
    className: cn(
      'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset',
      className,
      {
        'bg-gray-50 text-gray-600 ring-gray-500/10': variant === 'default',
        'bg-red-50 text-red-700 ring-red-600/10': variant === 'danger',
        'bg-orange-50 text-orange-800 ring-orange-600/20':
          variant === 'warning',
        'bg-green-50 text-green-700 ring-green-600/20': variant === 'success',
        'bg-blue-50 text-blue-700 ring-blue-700/10': variant === 'primary',
        'bg-indigo-50 text-indigo-700 ring-indigo-700/10':
          variant === 'secondary',
      },
    ),
    title,
    id,
    'data-testid': 'badge',
    ...props,
  };

  if (isRemovable) {
    return (
      <span {...commonProps} className={cn(commonProps.className, 'gap-1')}>
        {childElement}
        <span className="max-w-[12rem] truncate">{text}</span>
        <button
          type="button"
          className="ml-0.5 inline-flex items-center justify-center rounded-full p-0.5 transition-colors hover:bg-black/5 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-current"
          onClick={onRemove}
          title={removeTitle || `Remove ${text}`}
          aria-label={removeTitle || `Remove ${text}`}
        >
          <XMarkIcon className="h-3 w-3" aria-hidden="true" />
        </button>
      </span>
    );
  }

  if (isInteractive) {
    return (
      <button
        type="button"
        {...commonProps}
        onClick={onClick}
        tabIndex={0}
        // Support keyboard and touch natively
      >
        {childElement}
        {text}
      </button>
    );
  }

  return (
    <span {...commonProps}>
      {childElement}
      {text}
    </span>
  );
};
